// =============================================================================
//  Copyright   : 2018 semantics GmbH
//  Date        : $Date: 2026-05-25 21:18:10 +0200 (Mon, 25 May 2026) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 163265 $
//  Purpose     : uix root sagas
// =============================================================================
// ---Third-party imports
import { eventChannel, END } from 'redux-saga';
import { delay, call, put, take, all, select, fork } from 'redux-saga/effects';
import now from 'performance-now';
// ---Framework imports
import { rstrip, has } from 'ldash';
import log from 'uix/logging';
import { Sel, Actions } from 'uix/registries';
import { isSSR } from 'uix/utils';
// ---Globals
let SOCK = null;
let IN_SHUTDOWN = false;
// on docker-container restart we need to reconnect fast to respawning wsapi
// we want to capture the SERVER/STARTUP message
const BACKOFF_FACTOR = 1.1;
const INITIAL_BACKOFF = 1000;
const WS_PING_INTERVAL = 2000 // send a custom ping msg each 2000ms
;
// if ping roundtrip exceeds this value, assume offline
// ping rountrip time is affected by heavy cpu load, so deriving offline from
// rountrip time could be a bad idea
export const WS_PING_OFFLINE = 10000;
// status codes (on close)
// https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent#Status_codes
// https://www.iana.org/assignments/websocket/websocket.xml#close-code-number
const NORMAL_CLOSURE = 1000;
const GOING_AWAY = 1001;
const isCurrent = (other)=>other === SOCK;
const isOpen = (s)=>s?.readyState === WebSocket.OPEN;
const isOpenConnecting = (s)=>[
        WebSocket.OPEN,
        WebSocket.CONNECTING
    ].includes(s?.readyState);
const runPing = (sock, emit)=>{
    let start_ping = null;
    sock.on_pong = ()=>{
        if (start_ping) {
            let roundtrip = now() - start_ping;
            emit(Actions.setWebsocketState({
                roundtrip
            }));
            start_ping = null;
        }
    };
    const pingSocket = ()=>setTimeout(()=>{
            if (isOpen(sock)) {
                if (start_ping !== null) {
                    // update roundtrip time if no pong received
                    emit(Actions.setWebsocketState({
                        roundtrip: now() - start_ping
                    }));
                } else {
                    // only reset if received a pong
                    start_ping = now();
                }
                // always send a new ping
                sock.send('ping');
            }
            // compare with current global and restart if still current
            if (isCurrent(sock)) {
                pingSocket();
            }
        }, WS_PING_INTERVAL);
    // start
    pingSocket();
};
const createSocketChannel = (ws_url, user, instance)=>{
    /*`eventChannel` takes a subscriber function
    the subscriber function takes an `emit` argument to put messages onto the channel
  */ let sid = user.session;
    let more_reg = {
        username: user.name
    };
    if (user.groups.includes('debug')) {
        more_reg = {
            isDebug: true
        };
    }
    return eventChannel((emit)=>{
        let backoff = INITIAL_BACKOFF;
        let reconnect = true;
        let connect_failed = false;
        let is_online = false;
        let connect = ()=>{
            let socket = new WebSocket(ws_url);
            SOCK = socket;
            socket.onopen = (evt)=>{
                log.api('connected to instance %o via %o', instance, ws_url);
                // XXX: we get sometimes still a CONNECTING state here
                // hack away with decaying delay
                let register = (delay)=>setTimeout(()=>{
                        if (!isCurrent(socket)) {
                            return;
                        }
                        // register session at remote api endpoint (should also use action format?)
                        if (isOpen(socket)) {
                            socket.send(JSON.stringify({
                                register: 'ui',
                                sid,
                                instance,
                                ...more_reg
                            }));
                            //~ log.debug('ws registration send')
                            runPing(socket, emit);
                            // reset backoff
                            backoff = INITIAL_BACKOFF;
                            emit(Actions.setWebsocketState({
                                state: 'online'
                            }));
                            is_online = true;
                        } else {
                            if (delay < 3600) {
                                log.warn('retry websocket registration');
                                register(delay * 2);
                            } else {
                                log.error('websocket registration failed');
                            }
                        }
                    }, delay);
                register(50);
            };
            socket.onerror = (evt)=>{
                log.warn('cant connect to %o', ws_url);
                connect_failed = true;
                if (is_online && !reconnect) {
                    // was online - goes offline
                    emit(Actions.setWebsocketState({
                        state: 'offline'
                    }));
                    is_online = false;
                }
            };
            socket.onclose = (evt)=>{
                if (!connect_failed || is_online) {
                    let wstate = {
                        state: 'offline'
                    };
                    if (evt.code == GOING_AWAY) {
                        // does not trigger "offline ui box"
                        wstate.state = 'going_away';
                    }
                    if (evt.code === NORMAL_CLOSURE) {
                        // roundtrip 0 prevent API_OFFLINE detection
                        wstate.roundtrip = 0;
                    }
                    emit(Actions.setWebsocketState(wstate));
                    log.api('WebSocket: closed, code %o [%o]', evt.code, evt.reason);
                    is_online = false;
                }
                if (isCurrent(socket) && reconnect && !IN_SHUTDOWN) {
                    log.api(`WebSocket: reconnect in ${(backoff / 1000).toFixed(1)} second.`);
                    setTimeout(()=>{
                        connect();
                        backoff = Math.min(backoff * BACKOFF_FACTOR, 60000);
                    }, backoff);
                } else {
                    emit(END);
                }
            };
            socket.onmessage = (e)=>{
                if (e.data == 'pong') {
                    if (socket.on_pong) socket.on_pong();
                } else {
                    // assume message is a json encoded redux action in {type, payload, meta} format
                    emit(JSON.parse(e.data));
                }
            };
            return socket;
        };
        const sock = connect();
        // the subscriber must return an unsubscribe function
        // this will be invoked when the saga calls `channel.close` method
        const unsubscribe = ()=>{
            log.api('unsubscribe websocket channel');
            reconnect = false;
            sock.close();
        };
        return unsubscribe;
    });
};
const buildSocketUrl = (apicfg, apiurl)=>{
    try {
        if (!apicfg?.websocket) {
            return null;
        }
        let ws_url = apicfg.websocketUrl;
        if (!ws_url) {
            let parts = apiurl.baseUrl().split('//');
            let no_proto = parts.slice(1).join('//');
            // adapt to deployment situation
            if (!parts[0] && !isSSR) {
                ws_url = (window.location.protocol === 'https:' ? 'wss://' : 'ws://') + window.location.host + '/wsapi';
            } else if (no_proto.startsWith('localhost')) {
                // dev mode default fallback
                ws_url = 'ws://localhost:1090';
            } else {
                //~ let proto = !parts[0] || parts[0] === 'https' ? 'wss' : 'ws'
                ws_url = 'wss://' + no_proto + '/wsapi';
            }
        }
        return ws_url;
    } catch (e) {
        log.error(e);
    }
    return null;
};
function* processWebsocket() {
    /* we should reconnect, if these values change
   */ let apiurl = yield select(Sel.apiurl);
    let apicfg = yield select((root)=>root.config.instance.api);
    let instance = yield select((root)=>root.config.instance.info.id);
    let user = yield select(Sel.user);
    let ws_url = buildSocketUrl(apicfg, apiurl);
    if (!ws_url) {
        return;
    }
    try {
        const socketChannel = yield call(createSocketChannel, ws_url, user, instance);
        while(true){
            // receive actions from channel
            const action = yield take(socketChannel);
            if (!(has(action, 'type') && has(action, 'payload'))) {
                // incoming actions from websocket, check them
                log.error('invalid action format (need FSA, at least .payload and .type)');
            } else {
                yield put(action);
            }
        }
    } catch (e) {
        log.error(e);
    }
}
export function* connectWebsocket() {
    /* connect/reconnect websocket
   */ if (process.env.UIX_ENV === 'express') return;
    // we have saga channel unsubscribe handling - isnt this enough?
    window.addEventListener('pagehide', function(event) {
        IN_SHUTDOWN = true;
        SOCK.close(NORMAL_CLOSURE);
    //~ if (event.persisted === true) {
    //~ console.log('This page *might* be entering the bfcache.');
    //~ } else {
    //~ console.log('This page will unload normally and be discarded.');
    //~ }
    });
    if (isOpenConnecting(SOCK)) {
        // close old socket
        try {
            SOCK.close(NORMAL_CLOSURE);
        } catch (e) {
            log.error(e);
        } finally{
            SOCK = null;
        }
    }
    // setup new channel
    yield processWebsocket();
}
